import React from "react";
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import logo from '../../../assets/logo.svg';

const MA_CONFIG = {
    sma10:  { name: '10-DMA' },
    sma50:  { name: '50-DMA' },
    sma200: { name: '200-DMA' },
    ema21:  { name: '21-DEMA' },
};

// Small label rendered as a plain div ABOVE each control — avoids
// MUI floating label clipping issues entirely
const CtrlLabel = ({ children }) => (
    <div style={{
        fontSize: 10,
        fontWeight: 600,
        color: '#94a3b8',
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        marginBottom: 3,
        whiteSpace: 'nowrap',
    }}>
        {children}
    </div>
);

// Wrapper that stacks label + control vertically
const Ctrl = ({ label, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <CtrlLabel>{label}</CtrlLabel>
        {children}
    </div>
);

const ChartingTopbar = ({
    allSymbols, selectedSymbol, onSymbolSelection,
    chartType, onChartTypeChange,
    interval, onIntervalChange,
    timeframe, onTimeframeChange,
    selectedMAs, onMAChange,
    selectedOscillators, onOscillatorChange,
    fromDate, toDate, onFromDateChange, onToDateChange,
}) => {
    return (
        <header style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 20px',
            height: 72,
            background: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            gap: 12,
            boxSizing: 'border-box',
        }}>
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <div>
                <a className="navbar-brand" href="/home">
                    <img src={logo} alt="Capital Trends" />
                </a>
                    <div style={{ color: '#475569', fontSize: 10, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Advanced Charting</div>
                </div>
            </div>

            {/* Controls — scrollable row */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'nowrap',
                overflowX: 'auto',
                overflowY: 'visible',
                flex: 1,
                justifyContent: 'flex-end',
                // hide scrollbar but allow scroll
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
            }}>
                <Ctrl label="Symbol">
                    <Autocomplete
                        disablePortal
                        options={allSymbols}
                        sx={{ width: 180, ...acSx }}
                        renderInput={p => (
                            <TextField
                                {...p}
                                size="small"
                                placeholder="Symbol"
                                sx={tfSx}
                                InputLabelProps={{ shrink: false }}
                                label=""
                            />
                        )}
                        onChange={(_, v) => onSymbolSelection(v)}
                        value={selectedSymbol}
                        isOptionEqualToValue={(o, v) => o === v}
                        size="small"
                        ListboxProps={{ style: { background: '#ffffff', color: '#111827', fontSize: 13, border: '1px solid #e2e8f0' } }}
                    />
                </Ctrl>

                <Ctrl label="Interval">
                    <Select
                        value={interval}
                        onChange={onIntervalChange}
                        size="small"
                        sx={{ ...selSx, minWidth: 110 }}
                        input={<OutlinedInput />}
                    >
                        <MenuItem value="1D">1 Day</MenuItem>
                        <MenuItem value="5D">5 Days</MenuItem>
                        <MenuItem value="1M">1 Month</MenuItem>
                        <MenuItem value="3M">3 Months</MenuItem>
                        <MenuItem value="6M">6 Months</MenuItem>
                        <MenuItem value="12M">12 Months</MenuItem>
                        <MenuItem value="1Y">1 Year</MenuItem>
                        <MenuItem value="3Y">3 Years</MenuItem>
                        <MenuItem value="5Y">5 Years</MenuItem>
                        {interval === 'custom' && <MenuItem value="custom">Custom</MenuItem>}
                    </Select>
                </Ctrl>

                <Ctrl label="Chart Type">
                    <Select
                        value={chartType}
                        onChange={onChartTypeChange}
                        size="small"
                        sx={{ ...selSx, minWidth: 130 }}
                        input={<OutlinedInput />}
                    >
                        <MenuItem value="candles">Candles</MenuItem>
                        <MenuItem value="bars">Colored Bar</MenuItem>
                    </Select>
                </Ctrl>

                <Ctrl label="Timeframe">
                    <Select
                        value={timeframe}
                        onChange={onTimeframeChange}
                        size="small"
                        sx={{ ...selSx, minWidth: 120 }}
                        input={<OutlinedInput />}
                    >
                        <MenuItem value="1D">1 Day</MenuItem>
                        <MenuItem value="7D">Weekly</MenuItem>
                        <MenuItem value="30D">30 Day</MenuItem>
                    </Select>
                </Ctrl>

                <Ctrl label="Indicators">
                    <Select
                        multiple
                        value={selectedMAs || []}
                        onChange={onMAChange}
                        size="small"
                        sx={{ ...selSx, minWidth: 150 }}
                        input={<OutlinedInput />}
                        renderValue={(s) => {
                            if (!Array.isArray(s) || s.length === 0) return 'None';
                            return s.map(k => MA_CONFIG[k]?.name).join(', ');
                        }}
                        displayEmpty
                    >
                        <MenuItem disabled sx={{ fontSize: 11, opacity: 0.5 }}>─ Simple MAs ─</MenuItem>
                        <MenuItem value="sma10">10-DMA (SMA)</MenuItem>
                        <MenuItem value="sma50">50-DMA (SMA)</MenuItem>
                        <MenuItem value="sma200">200-DMA (SMA)</MenuItem>
                        <MenuItem disabled sx={{ fontSize: 11, opacity: 0.5 }}>─ Exponential ─</MenuItem>
                        <MenuItem value="ema21">21-DEMA (EMA)</MenuItem>
                    </Select>
                </Ctrl>

                <Ctrl label="Oscillators">
                    <Select
                        multiple
                        value={selectedOscillators || []}
                        onChange={onOscillatorChange}
                        size="small"
                        sx={{ ...selSx, minWidth: 130 }}
                        input={<OutlinedInput />}
                        renderValue={(s) => {
                            if (!Array.isArray(s) || s.length === 0) return 'None';
                            return s.map(x => x.toUpperCase()).join(', ');
                        }}
                        displayEmpty
                    >
                        <MenuItem value="rsi">RSI (14)</MenuItem>
                        <MenuItem value="macd">MACD (12, 26, 9)</MenuItem>
                    </Select>
                </Ctrl>

                <Ctrl label="From Date">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={fromDate ? dayjs(fromDate) : null}
                            onChange={onFromDateChange}
                            maxDate={dayjs()}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    label: '',
                                    InputLabelProps: { shrink: false },
                                    sx: { width: 140, ...tfSx },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Ctrl>

                <Ctrl label="To Date">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={toDate ? dayjs(toDate) : null}
                            onChange={onToDateChange}
                            maxDate={dayjs()}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    label: '',
                                    InputLabelProps: { shrink: false },
                                    sx: { width: 140, ...tfSx },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Ctrl>
            </div>
        </header>
    );
};

// ── Shared dark-theme MUI sx objects ─────────────────────────────────────────
const selSx = {
    color: '#111827',
    fontSize: 13,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' },
    '& .MuiSvgIcon-root': { color: '#94a3b8' },
    background: '#f8fafc',
    borderRadius: '6px',
};

const tfSx = {
    '& .MuiOutlinedInput-root': {
        color: '#111827',
        fontSize: 13,
        background: '#f8fafc',
        borderRadius: '6px',
        '& fieldset': { borderColor: '#e2e8f0' },
        '&:hover fieldset': { borderColor: '#94a3b8' },
        '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: '#94a3b8' },
    '& .MuiInputBase-input::placeholder': { color: '#94a3b8', opacity: 1 },
};

const acSx = {
    '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#64748b' },
};

export default ChartingTopbar;
